import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useSearchParams } from 'react-router-dom';
import { Layout } from '../../components/layout/Layout';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Switch } from '../../components/ui/switch';
import { Label } from '../../components/ui/label';
import { Avatar, AvatarFallback, AvatarImage } from '../../components/ui/avatar';
import { Separator } from '../../components/ui/separator';
import { toast } from 'sonner';
import { 
  User, 
  Camera, 
  Bell, 
  Mail, 
  MessageSquare, 
  Calendar, 
  Megaphone,
  Ticket,
  Loader2,
  Trash2,
  Upload,
  CreditCard,
  ExternalLink,
  CheckCircle2,
  AlertCircle
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';

const API_URL = process.env.NODE_ENV === 'production' ? '' : (process.env.REACT_APP_BACKEND_URL || '');

export default function ProfileSettings() {
  const { user, refreshUser, isOrganizer } = useAuth();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savingOrganizerSettings, setSavingOrganizerSettings] = useState(false);
  const fileInputRef = useRef(null);
  
  // Stripe Connect state
  const [stripeStatus, setStripeStatus] = useState(null);
  const [stripeLoading, setStripeLoading] = useState(false);
  
  const [notifications, setNotifications] = useState({
    email_notifications: true,
    sms_notifications: true,
    event_reminders: true,
    marketing_emails: false,
    ticket_updates: true
  });

  const [organizerNotifications, setOrganizerNotifications] = useState({
    notification_email: '',
    email_on_ticket_purchase: true,
    email_on_withdrawal: true,
    email_on_event_reminder: true
  });

  const fetchedRef = useRef(false);
  
  useEffect(() => {
    if (fetchedRef.current) return;
    fetchedRef.current = true;
    
    fetchNotificationPreferences();
    if (isOrganizer || user?.role === 'admin') {
      fetchOrganizerNotificationSettings();
      fetchStripeStatus();
    }
    
    // Check for Stripe return params
    const stripeParam = searchParams.get('stripe');
    if (stripeParam === 'success') {
      toast.success('Stripe account setup complete!');
      fetchStripeStatus();
    } else if (stripeParam === 'refresh') {
      toast.info('Please complete your Stripe account setup');
      handleStripeOnboarding();
    }
  }, [isOrganizer, user?.role]);
  
  // Fetch Stripe Connect status
  const fetchStripeStatus = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/stripe/connect/status`);
      setStripeStatus(response.data);
    } catch (error) {
      console.error('Error fetching Stripe status:', error);
    }
  };
  
  // Start Stripe onboarding
  const handleStripeOnboarding = async () => {
    setStripeLoading(true);
    try {
      const response = await axios.post(`${API_URL}/api/stripe/connect/onboard`, {});
      if (response.data.onboarding_url) {
        window.location.href = response.data.onboarding_url;
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start Stripe setup');
      setStripeLoading(false);
    }
  };
  
  // Open Stripe dashboard
  const handleStripeDashboard = async () => {
    setStripeLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/stripe/connect/dashboard`);
      if (response.data.dashboard_url) {
        window.open(response.data.dashboard_url, '_blank');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to open Stripe dashboard');
    } finally {
      setStripeLoading(false);
    }
  };

  const fetchOrganizerNotificationSettings = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/organizer/notification-settings`);
      if (response.data) {
        setOrganizerNotifications(prev => ({
          notification_email: response.data.notification_email ?? prev.notification_email,
          email_on_ticket_purchase: response.data.email_on_ticket_purchase ?? prev.email_on_ticket_purchase,
          email_on_withdrawal: response.data.email_on_withdrawal ?? prev.email_on_withdrawal,
          email_on_event_reminder: response.data.email_on_event_reminder ?? prev.email_on_event_reminder
        }));
      }
    } catch (error) {
      console.error('Error fetching organizer notification settings:', error);
    }
  };

  const saveOrganizerNotificationSettings = async () => {
    setSavingOrganizerSettings(true);
    try {
      const response = await axios.post(`${API_URL}/api/organizer/notification-settings`, organizerNotifications);
      if (response.data.settings) {
        setOrganizerNotifications(prev => ({
          notification_email: response.data.settings.notification_email ?? prev.notification_email,
          email_on_ticket_purchase: response.data.settings.email_on_ticket_purchase ?? prev.email_on_ticket_purchase,
          email_on_withdrawal: response.data.settings.email_on_withdrawal ?? prev.email_on_withdrawal,
          email_on_event_reminder: response.data.settings.email_on_event_reminder ?? prev.email_on_event_reminder
        }));
      }
      toast.success('Notification settings saved');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save notification settings');
    } finally {
      setSavingOrganizerSettings(false);
    }
  };

  const fetchNotificationPreferences = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/auth/notifications`);
      const prefs = response.data.notification_preferences || response.data;
      setNotifications(prev => ({ ...prev, ...prefs }));
    } catch (error) {
      console.error('Error fetching preferences:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePictureUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const validTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      toast.error('Please upload a valid image (JPEG, PNG, GIF, or WebP)');
      return;
    }

    // Validate file size (2MB max)
    if (file.size > 2 * 1024 * 1024) {
      toast.error('Image must be less than 2MB');
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(`${API_URL}/api/auth/profile/picture`, formData, {
        
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      toast.success('Profile picture updated!');
      if (refreshUser) refreshUser();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload picture');
    } finally {
      setUploading(false);
    }
  };

  const handleDeletePicture = async () => {
    try {
      await axios.delete(`${API_URL}/api/auth/profile/picture`);
      toast.success('Profile picture removed');
      if (refreshUser) refreshUser();
    } catch (error) {
      toast.error('Failed to remove picture');
    }
  };

  const handleNotificationChange = (key, value) => {
    setNotifications(prev => ({ ...prev, [key]: value }));
  };

  const saveNotifications = async () => {
    setSaving(true);
    try {
      await axios.put(`${API_URL}/api/auth/notifications`, notifications);
      toast.success('Notification preferences saved!');
    } catch (error) {
      toast.error('Failed to save preferences');
    } finally {
      setSaving(false);
    }
  };

  const getInitials = (name) => {
    if (!name) return 'U';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  const getProfileImageUrl = (picture) => {
    if (!picture) return null;
    if (picture.startsWith('http')) return picture;
    return `${API_URL}${picture}`;
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="container mx-auto px-4 py-8 max-w-2xl" data-testid="profile-settings-page">
        {/* Header */}
        <div className="flex items-center gap-3 mb-8">
          <div className="p-3 rounded-xl bg-primary/10">
            <User className="w-6 h-6 text-primary" />
          </div>
          <div>
            <h1 className="text-3xl font-bold">Profile Settings</h1>
            <p className="text-muted-foreground">Manage your account preferences</p>
          </div>
        </div>

        {/* Profile Picture Section */}
        <Card className="mb-6 bg-card/50 border-border/50">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Camera className="w-5 h-5" />
              Profile Picture
            </CardTitle>
            <CardDescription>Upload a photo to personalize your account</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-6">
              <div className="relative">
                <Avatar className="w-24 h-24 border-4 border-primary/20">
                  <AvatarImage src={getProfileImageUrl(user?.picture)} alt={user?.name} />
                  <AvatarFallback className="text-2xl bg-primary/20">
                    {getInitials(user?.name)}
                  </AvatarFallback>
                </Avatar>
                {uploading && (
                  <div className="absolute inset-0 flex items-center justify-center bg-black/50 rounded-full">
                    <Loader2 className="w-8 h-8 animate-spin text-white" />
                  </div>
                )}
              </div>
              
              <div className="flex flex-col gap-2">
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handlePictureUpload}
                  accept="image/jpeg,image/png,image/gif,image/webp"
                  className="hidden"
                  data-testid="profile-picture-input"
                />
                <Button
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                  className="gap-2"
                  data-testid="upload-picture-btn"
                >
                  <Upload className="w-4 h-4" />
                  {uploading ? 'Uploading...' : 'Upload Photo'}
                </Button>
                {user?.picture && (
                  <Button
                    variant="outline"
                    onClick={handleDeletePicture}
                    className="gap-2 text-destructive hover:text-destructive"
                    data-testid="delete-picture-btn"
                  >
                    <Trash2 className="w-4 h-4" />
                    Remove
                  </Button>
                )}
                <p className="text-xs text-muted-foreground">
                  Max 2MB. JPEG, PNG, GIF, or WebP.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Organizer Notification Settings - Only for Organizers/Admins */}
        {(isOrganizer || user?.role === 'admin') && (
          <Card className="mb-6 bg-card/50 border-border/50" data-testid="organizer-notification-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Ticket className="w-5 h-5 text-primary" />
                Sales Notifications
              </CardTitle>
              <CardDescription>Get notified when someone purchases tickets for your events</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Notification Email Input */}
              <div className="space-y-2">
                <Label htmlFor="notification_email" className="font-medium">Notification Email <span className="text-destructive">*</span></Label>
                <p className="text-sm text-muted-foreground mb-2">
                  Required — this email receives ticket sale notifications and must be set before publishing events
                </p>
                <div className="flex gap-2">
                  <input
                    type="email"
                    id="notification_email"
                    value={organizerNotifications.notification_email}
                    onChange={(e) => setOrganizerNotifications(prev => ({
                      ...prev,
                      notification_email: e.target.value
                    }))}
                    placeholder="your@email.com"
                    className="flex-1 px-3 py-2 rounded-md border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                    data-testid="notification-email-input"
                    required
                  />
                </div>
              </div>

              <Separator />

              {/* Ticket Purchase Notifications */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-green-500/10">
                    <Ticket className="w-4 h-4 text-green-500" />
                  </div>
                  <div>
                    <Label htmlFor="email_on_ticket_purchase" className="font-medium">Ticket Purchase Alerts</Label>
                    <p className="text-sm text-muted-foreground">Get notified when someone buys a ticket</p>
                  </div>
                </div>
                <Switch
                  id="email_on_ticket_purchase"
                  checked={organizerNotifications.email_on_ticket_purchase}
                  onCheckedChange={(checked) => setOrganizerNotifications(prev => ({
                    ...prev,
                    email_on_ticket_purchase: checked
                  }))}
                  data-testid="ticket-purchase-switch"
                />
              </div>

              <Separator />

              {/* Withdrawal Notifications */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-blue-500/10">
                    <Mail className="w-4 h-4 text-blue-500" />
                  </div>
                  <div>
                    <Label htmlFor="email_on_withdrawal" className="font-medium">Withdrawal Updates</Label>
                    <p className="text-sm text-muted-foreground">Get notified about withdrawal status changes</p>
                  </div>
                </div>
                <Switch
                  id="email_on_withdrawal"
                  checked={organizerNotifications.email_on_withdrawal}
                  onCheckedChange={(checked) => setOrganizerNotifications(prev => ({
                    ...prev,
                    email_on_withdrawal: checked
                  }))}
                  data-testid="withdrawal-switch"
                />
              </div>

              <Separator />

              {/* Event Reminder Notifications */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-purple-500/10">
                    <Calendar className="w-4 h-4 text-purple-500" />
                  </div>
                  <div>
                    <Label htmlFor="email_on_event_reminder" className="font-medium">Event Reminders</Label>
                    <p className="text-sm text-muted-foreground">Get reminders about your upcoming events</p>
                  </div>
                </div>
                <Switch
                  id="email_on_event_reminder"
                  checked={organizerNotifications.email_on_event_reminder}
                  onCheckedChange={(checked) => setOrganizerNotifications(prev => ({
                    ...prev,
                    email_on_event_reminder: checked
                  }))}
                  data-testid="event-reminder-switch"
                />
              </div>

              <div className="pt-4">
                <Button
                  onClick={saveOrganizerNotificationSettings}
                  disabled={savingOrganizerSettings}
                  className="w-full gap-2"
                  data-testid="save-organizer-notifications-btn"
                >
                  {savingOrganizerSettings ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    'Save Sales Notification Settings'
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Notification Preferences Section */}
        <Card className="bg-card/50 border-border/50">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Bell className="w-5 h-5" />
              Notification Preferences
            </CardTitle>
            <CardDescription>Control how we communicate with you</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Email Notifications */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-blue-500/10">
                  <Mail className="w-4 h-4 text-blue-500" />
                </div>
                <div>
                  <Label htmlFor="email_notifications" className="font-medium">Email Notifications</Label>
                  <p className="text-sm text-muted-foreground">Receive updates via email</p>
                </div>
              </div>
              <Switch
                id="email_notifications"
                checked={notifications.email_notifications}
                onCheckedChange={(checked) => handleNotificationChange('email_notifications', checked)}
                data-testid="email-notifications-switch"
              />
            </div>

            <Separator />

            {/* SMS Notifications */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-green-500/10">
                  <MessageSquare className="w-4 h-4 text-green-500" />
                </div>
                <div>
                  <Label htmlFor="sms_notifications" className="font-medium">SMS Notifications</Label>
                  <p className="text-sm text-muted-foreground">Receive important updates via SMS</p>
                </div>
              </div>
              <Switch
                id="sms_notifications"
                checked={notifications.sms_notifications}
                onCheckedChange={(checked) => handleNotificationChange('sms_notifications', checked)}
                data-testid="sms-notifications-switch"
              />
            </div>

            <Separator />

            {/* Event Reminders */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-purple-500/10">
                  <Calendar className="w-4 h-4 text-purple-500" />
                </div>
                <div>
                  <Label htmlFor="event_reminders" className="font-medium">Event Reminders</Label>
                  <p className="text-sm text-muted-foreground">Get reminded before your events</p>
                </div>
              </div>
              <Switch
                id="event_reminders"
                checked={notifications.event_reminders}
                onCheckedChange={(checked) => handleNotificationChange('event_reminders', checked)}
                data-testid="event-reminders-switch"
              />
            </div>

            <Separator />

            {/* Ticket Updates */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-amber-500/10">
                  <Ticket className="w-4 h-4 text-amber-500" />
                </div>
                <div>
                  <Label htmlFor="ticket_updates" className="font-medium">Ticket Updates</Label>
                  <p className="text-sm text-muted-foreground">Get notified about ticket changes</p>
                </div>
              </div>
              <Switch
                id="ticket_updates"
                checked={notifications.ticket_updates}
                onCheckedChange={(checked) => handleNotificationChange('ticket_updates', checked)}
                data-testid="ticket-updates-switch"
              />
            </div>

            <Separator />

            {/* Marketing Emails */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-pink-500/10">
                  <Megaphone className="w-4 h-4 text-pink-500" />
                </div>
                <div>
                  <Label htmlFor="marketing_emails" className="font-medium">Marketing & Promotions</Label>
                  <p className="text-sm text-muted-foreground">Receive offers and recommendations</p>
                </div>
              </div>
              <Switch
                id="marketing_emails"
                checked={notifications.marketing_emails}
                onCheckedChange={(checked) => handleNotificationChange('marketing_emails', checked)}
                data-testid="marketing-emails-switch"
              />
            </div>

            <div className="pt-4">
              <Button
                onClick={saveNotifications}
                disabled={saving}
                className="w-full gap-2"
                data-testid="save-notifications-btn"
              >
                {saving ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Saving...
                  </>
                ) : (
                  'Save Preferences'
                )}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Stripe Connect Section - Organizers Only */}
        {(isOrganizer || user?.role === 'admin') && (
          <Card data-testid="stripe-connect-card">
            <CardHeader>
              <div className="flex items-center gap-2">
                <CreditCard className="w-5 h-5 text-primary" />
                <CardTitle>Stripe Payments</CardTitle>
              </div>
              <CardDescription>
                Connect your Stripe account to receive ticket payments directly
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {stripeStatus?.connected ? (
                <>
                  {/* Connected Status */}
                  <div className={`p-4 rounded-lg border ${
                    stripeStatus.status === 'active' 
                      ? 'bg-green-500/10 border-green-500/30' 
                      : 'bg-yellow-500/10 border-yellow-500/30'
                  }`}>
                    <div className="flex items-start gap-3">
                      {stripeStatus.status === 'active' ? (
                        <CheckCircle2 className="w-5 h-5 text-green-500 mt-0.5" />
                      ) : (
                        <AlertCircle className="w-5 h-5 text-yellow-500 mt-0.5" />
                      )}
                      <div className="flex-1">
                        <p className="font-medium">
                          {stripeStatus.status === 'active' 
                            ? 'Stripe Account Connected' 
                            : 'Setup Incomplete'}
                        </p>
                        <p className="text-sm text-muted-foreground mt-1">
                          {stripeStatus.message}
                        </p>
                        
                        {/* Capabilities */}
                        <div className="flex flex-wrap gap-2 mt-3">
                          <span className={`text-xs px-2 py-1 rounded ${
                            stripeStatus.charges_enabled 
                              ? 'bg-green-500/20 text-green-600' 
                              : 'bg-gray-500/20 text-gray-500'
                          }`}>
                            {stripeStatus.charges_enabled ? '✓' : '○'} Card Payments
                          </span>
                          <span className={`text-xs px-2 py-1 rounded ${
                            stripeStatus.payouts_enabled 
                              ? 'bg-green-500/20 text-green-600' 
                              : 'bg-gray-500/20 text-gray-500'
                          }`}>
                            {stripeStatus.payouts_enabled ? '✓' : '○'} Payouts
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Actions */}
                  <div className="flex flex-wrap gap-3">
                    {stripeStatus.status !== 'active' && (
                      <Button
                        onClick={handleStripeOnboarding}
                        disabled={stripeLoading}
                        className="gap-2"
                        data-testid="complete-stripe-setup-btn"
                      >
                        {stripeLoading ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <ExternalLink className="w-4 h-4" />
                        )}
                        Complete Setup
                      </Button>
                    )}
                    <Button
                      variant="outline"
                      onClick={handleStripeDashboard}
                      disabled={stripeLoading}
                      className="gap-2"
                      data-testid="stripe-dashboard-btn"
                    >
                      {stripeLoading ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <ExternalLink className="w-4 h-4" />
                      )}
                      Stripe Dashboard
                    </Button>
                  </div>
                </>
              ) : (
                <>
                  {/* Not Connected */}
                  <div className="p-4 rounded-lg bg-muted/50 border border-border">
                    <p className="text-sm text-muted-foreground mb-3">
                      Connect your Stripe account to:
                    </p>
                    <ul className="space-y-2 text-sm">
                      <li className="flex items-center gap-2">
                        <CheckCircle2 className="w-4 h-4 text-primary" />
                        Receive ticket payments directly to your bank
                      </li>
                      <li className="flex items-center gap-2">
                        <CheckCircle2 className="w-4 h-4 text-primary" />
                        Get automatic payouts (daily or weekly)
                      </li>
                      <li className="flex items-center gap-2">
                        <CheckCircle2 className="w-4 h-4 text-primary" />
                        Accept card payments from anywhere
                      </li>
                    </ul>
                  </div>
                  
                  <Button
                    onClick={handleStripeOnboarding}
                    disabled={stripeLoading}
                    className="w-full gap-2"
                    data-testid="connect-stripe-btn"
                  >
                    {stripeLoading ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Setting up...
                      </>
                    ) : (
                      <>
                        <CreditCard className="w-4 h-4" />
                        Connect Stripe Account
                      </>
                    )}
                  </Button>
                  
                  <p className="text-xs text-center text-muted-foreground">
                    You'll be redirected to Stripe to complete the setup
                  </p>
                </>
              )}
            </CardContent>
          </Card>
        )}
      </div>
    </Layout>
  );
}
